import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ChangeEvent, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { useFeedback } from '@/components/feedback/feedback-context'
import { clearPortalSession } from '@/lib/auth/portal-session'
import {
  persistPortalLoginPrefillEmail,
  updateStoredPortalAccount,
} from '@/lib/auth/session-storage'

import {
  deleteUserAvatar,
  getUserProfile,
  updateUserPassword,
  updateUserProfile,
  uploadUserAvatar,
  type UserProfile,
} from './api'
import { resolveProfileErrorMessage } from './errors'

const profileQueryKey = ['user-profile', 'current'] as const
const avatarOutputSize = 512

export type ProfileTab = 'basic' | 'persona' | 'usage'

export interface PasswordFormState {
  confirmPassword: string
  currentPassword: string
  newPassword: string
}

export type PasswordErrors = Partial<Record<keyof PasswordFormState, string>>

export function getProfileInitial(profile: Pick<UserProfile, 'email' | 'name'>) {
  const source = profile.name.trim() || profile.email.trim()

  return Array.from(source)[0]?.toUpperCase() ?? 'U'
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()

    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('avatar image failed to load'))
    image.src = src
  })
}

async function createCroppedAvatarFile(sourceUrl: string, zoom: number) {
  const image = await loadImage(sourceUrl)
  const canvas = document.createElement('canvas')
  canvas.width = avatarOutputSize
  canvas.height = avatarOutputSize

  const context = canvas.getContext('2d')
  if (!context) {
    throw new Error('canvas is unavailable')
  }

  const normalizedZoom = Math.max(1, Math.min(2, zoom))
  const cropSize = Math.min(image.naturalWidth, image.naturalHeight) / normalizedZoom
  const sourceX = (image.naturalWidth - cropSize) / 2
  const sourceY = (image.naturalHeight - cropSize) / 2

  context.drawImage(
    image,
    sourceX,
    sourceY,
    cropSize,
    cropSize,
    0,
    0,
    avatarOutputSize,
    avatarOutputSize,
  )

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/png')
  })

  if (!blob) {
    throw new Error('avatar crop failed')
  }

  return new File([blob], 'avatar.png', { type: 'image/png' })
}

export function useProfileQuery() {
  return useQuery({
    queryFn: getUserProfile,
    queryKey: profileQueryKey,
  })
}

export function useProfileWorkspace(profile: UserProfile) {
  const { t } = useTranslation('userProfile')
  const queryClient = useQueryClient()
  const { showFeedback } = useFeedback()
  const [activeTab, setActiveTab] = useState<ProfileTab>('basic')
  const [avatarEditorOpen, setAvatarEditorOpen] = useState(false)
  const [nameEditorOpen, setNameEditorOpen] = useState(false)
  const [name, setName] = useState(profile.name)
  const [nameError, setNameError] = useState('')

  const syncProfile = (nextProfile: UserProfile) => {
    queryClient.setQueryData(profileQueryKey, nextProfile)
    updateStoredPortalAccount('user', {
      avatar: nextProfile.avatar ?? undefined,
      email: nextProfile.email,
      id: nextProfile.id,
      name: nextProfile.name,
      role: nextProfile.role,
      status: nextProfile.status,
    })
  }

  const saveProfileMutation = useMutation({
    mutationFn: updateUserProfile,
    onError: (error) => {
      showFeedback({
        message: resolveProfileErrorMessage(error, t),
        tone: 'error',
      })
    },
    onSuccess: (nextProfile) => {
      syncProfile(nextProfile)
      setNameEditorOpen(false)
      showFeedback({
        message: t('success.profileSaved'),
        tone: 'success',
      })
    },
  })

  const saveTimezoneMutation = useMutation({
    mutationFn: updateUserProfile,
    onError: (error) => {
      showFeedback({
        message: resolveProfileErrorMessage(error, t),
        tone: 'error',
      })
    },
    onSuccess: (nextProfile) => {
      syncProfile(nextProfile)
      showFeedback({
        message: t('success.timezoneSaved'),
        tone: 'success',
      })
    },
  })

  const uploadAvatarMutation = useMutation({
    mutationFn: uploadUserAvatar,
    onError: (error) => {
      showFeedback({
        message: resolveProfileErrorMessage(error, t),
        tone: 'error',
      })
    },
    onSuccess: (avatar) => {
      syncProfile({ ...profile, avatar })
      showFeedback({
        message: t('success.avatarUploaded'),
        tone: 'success',
      })
    },
  })

  const deleteAvatarMutation = useMutation({
    mutationFn: deleteUserAvatar,
    onError: (error) => {
      showFeedback({
        message: resolveProfileErrorMessage(error, t),
        tone: 'error',
      })
    },
    onSuccess: () => {
      syncProfile({ ...profile, avatar: null })
      showFeedback({
        message: t('success.avatarDeleted'),
        tone: 'success',
      })
    },
  })

  const selectTab = (tab: ProfileTab) => {
    setActiveTab(tab)
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
  }

  const openNameEditor = () => {
    setName(profile.name)
    setNameError('')
    setNameEditorOpen(true)
  }

  const closeNameEditor = () => {
    setName(profile.name)
    setNameError('')
    setNameEditorOpen(false)
  }

  const submitName = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()

    const normalizedName = name.trim()
    if (!normalizedName) {
      setNameError(t('validation.nameRequired'))
      window.requestAnimationFrame(() => {
        document.getElementById('profile-display-name')?.focus()
      })
      return
    }

    setNameError('')
    saveProfileMutation.mutate({ name: normalizedName })
  }

  return {
    activeTab,
    avatarEditorOpen,
    closeNameEditor,
    deleteAvatarMutation,
    name,
    nameEditorOpen,
    nameError,
    openNameEditor,
    saveProfileMutation,
    saveTimezoneMutation,
    selectTab,
    setAvatarEditorOpen,
    setName,
    setNameError,
    submitName,
    t,
    uploadAvatarMutation,
  }
}

export function useAvatarEditor({
  avatar,
  cropFailedMessage,
  isRemoving,
  isUploading,
  onClose,
  onRemove,
  onUpload,
}: {
  avatar: string | null
  cropFailedMessage: string
  isRemoving: boolean
  isUploading: boolean
  onClose: () => void
  onRemove: () => Promise<void>
  onUpload: (file: File) => Promise<void>
}) {
  const { t } = useTranslation('userProfile')
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const draftUrlRef = useRef('')
  const [draftUrl, setDraftUrl] = useState('')
  const [zoom, setZoom] = useState(1)
  const [cropError, setCropError] = useState('')

  const revokeDraftUrl = () => {
    if (draftUrlRef.current) {
      URL.revokeObjectURL(draftUrlRef.current)
      draftUrlRef.current = ''
    }
    setDraftUrl('')
  }

  const resetDraft = () => {
    revokeDraftUrl()
    setZoom(1)
    setCropError('')
  }

  useEffect(() => {
    return () => {
      if (draftUrlRef.current) {
        URL.revokeObjectURL(draftUrlRef.current)
      }
    }
  }, [])

  const closeAndReset = () => {
    resetDraft()
    onClose()
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) {
      return
    }

    const nextUrl = URL.createObjectURL(file)
    revokeDraftUrl()
    draftUrlRef.current = nextUrl
    setDraftUrl(nextUrl)
    setZoom(1)
    setCropError('')
  }

  const submitAvatar = async () => {
    if (!draftUrl) {
      fileInputRef.current?.click()
      return
    }

    let file: File

    try {
      file = await createCroppedAvatarFile(draftUrl, zoom)
    } catch {
      setCropError(cropFailedMessage)
      return
    }

    try {
      await onUpload(file)
      resetDraft()
    } catch {
      // 上传 mutation 已负责 API 错误 toast；这里不要把上传失败误报成裁切失败。
    }
  }

  const removeAvatar = async () => {
    await onRemove()
    resetDraft()
  }

  return {
    closeAndReset,
    cropError,
    draftUrl,
    fileInputRef,
    handleFileChange,
    isBusy: isUploading || isRemoving,
    previewAvatar: draftUrl || avatar,
    removeAvatar,
    setZoom,
    submitAvatar,
    t,
    zoom,
  }
}

export function usePasswordEditor({
  onClose,
  profileEmail,
}: {
  onClose: () => void
  profileEmail: string
}) {
  const { t } = useTranslation('userProfile')
  const { showFeedback } = useFeedback()
  const [passwordForm, setPasswordForm] = useState<PasswordFormState>({
    confirmPassword: '',
    currentPassword: '',
    newPassword: '',
  })
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({})

  const resetPasswordForm = () => {
    setPasswordForm({
      confirmPassword: '',
      currentPassword: '',
      newPassword: '',
    })
    setPasswordErrors({})
  }

  const closeAndReset = () => {
    resetPasswordForm()
    onClose()
  }

  const passwordMutation = useMutation({
    mutationFn: updateUserPassword,
    onError: (error) => {
      showFeedback({
        message: resolveProfileErrorMessage(error, t),
        tone: 'error',
      })
    },
    onSuccess: () => {
      showFeedback({
        message: t('success.passwordChanged'),
        tone: 'success',
      })
      persistPortalLoginPrefillEmail('user', profileEmail)
      clearPortalSession('user')
      window.location.assign('/login')
    },
  })

  const updatePasswordField = (field: keyof PasswordFormState, value: string) => {
    setPasswordForm((current) => ({ ...current, [field]: value }))
    setPasswordErrors((current) => ({ ...current, [field]: undefined }))
  }

  const validatePassword = () => {
    const nextErrors: PasswordErrors = {}

    if (!passwordForm.currentPassword.trim()) {
      nextErrors.currentPassword = t('validation.currentPasswordRequired')
    }

    if (passwordForm.newPassword.length < 8) {
      nextErrors.newPassword = t('validation.newPasswordLength')
    }

    if (passwordForm.confirmPassword !== passwordForm.newPassword) {
      nextErrors.confirmPassword = t('validation.confirmPasswordMismatch')
    }

    setPasswordErrors(nextErrors)

    const firstField = Object.keys(nextErrors)[0] as keyof PasswordFormState | undefined
    if (firstField) {
      window.requestAnimationFrame(() => {
        document.getElementById(`profile-${firstField}`)?.focus()
      })
    }

    return Object.keys(nextErrors).length === 0
  }

  const submitPassword = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validatePassword()) {
      return
    }

    passwordMutation.mutate({
      currentPassword: passwordForm.currentPassword,
      newPassword: passwordForm.newPassword,
    })
  }

  return {
    closeAndReset,
    passwordErrors,
    passwordForm,
    passwordMutation,
    submitPassword,
    t,
    updatePasswordField,
  }
}
